import { useEffect, useLayoutEffect, useState } from 'react';

/** There is nothing to measure on the server, where layout effects do not run. */
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Tracks an element's content-box width, so SVG geometry can be built in real pixels.
 * Measures before paint, so the fallback width is never the one on screen.
 */
export default function useMeasuredWidth<T extends HTMLElement>(ref: React.RefObject<T>, fallback = 880) {
    const [width, setWidth] = useState(fallback);

    useIsomorphicLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;

        const update = () => setWidth(el.getBoundingClientRect().width || fallback);
        update();

        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref, fallback]);

    return width;
}
